import { z } from "zod";
import { type ReplayTimingController } from "./use-replay-timing";

type PhaseTimingKey =
  | "bundleReadyAt"
  | "replayFetchStartAt"
  | "replayFetchEndAt"
  | "replayParsedAt";

const phaseTimingKey = new Map<string, PhaseTimingKey>([
  ["bundle_ready", "bundleReadyAt"],
  ["replay_fetch_start", "replayFetchStartAt"],
  ["replay_fetch_end", "replayFetchEndAt"],
  ["replay_parsed", "replayParsedAt"],
]);

const viewerMessageSchema = z.object({
  src: z.literal("coworld-replay"),
  type: z.string().optional(),
  message: z.unknown().optional(),
  diagnostics: z.unknown().optional(),
  phase: z.unknown().optional(),
  bytes: z.unknown().optional(),
  compressed: z.unknown().optional(),
  tick: z.unknown().optional(),
  playing: z.unknown().optional(),
});
export type ViewerMessage = Readonly<z.infer<typeof viewerMessageSchema>>;

export function parseViewerMessage(value: unknown): ViewerMessage | null {
  const parsed = viewerMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function recordReplayPhase({
  data,
  recordTiming,
}: Readonly<{
  data: ViewerMessage;
  recordTiming: ReplayTimingController["recordTiming"];
}>): void {
  const key =
    typeof data.phase === "string" ? phaseTimingKey.get(data.phase) : undefined;

  if (key === undefined) return;

  recordTiming({ [key]: performance.now() });

  if (key === "replayFetchEndAt") {
    recordTiming({
      replayBytes: typeof data.bytes === "number" ? data.bytes : undefined,
      replayCompressed:
        typeof data.compressed === "boolean" ? data.compressed : undefined,
    });
  }
}

export function validTick(data: ViewerMessage): data is ViewerMessage & {
  tick: number;
  playing: boolean;
} {
  return (
    typeof data.tick === "number" &&
    Number.isInteger(data.tick) &&
    data.tick >= 0 &&
    typeof data.playing === "boolean"
  );
}

export type ReplayTick = Readonly<{ tick: number; playing: boolean }>;
